import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { fetchAll } from '../lib/fetchAll'
import { Icon, Avatar, StatusPill, Spinner, EmptyState, timeAgo, fmtDateTime, normStatus } from './ui'

const EMAIL_RE = /^[^\s@,;<>"()]+@[^\s@,;<>"()]+\.[^\s@,;<>"()]+$/
const norm = (e) => String(e || '').trim().toLowerCase()
const MISSING_SQL = 'Le tabelle dei clienti non esistono ancora: esegui portal_schema.sql nel SQL Editor di Supabase.'

// Stato dell'accesso all'area clienti
function accountState(acc) {
  if (!acc) return null
  if (acc.disabled) return { label: 'Bloccato', color: 'var(--red)', bg: 'var(--red-soft)' }
  if (!acc.verified_at) return { label: 'Email da confermare', color: 'var(--amber)', bg: 'var(--amber-soft)' }
  return { label: 'Attivo', color: 'var(--green)', bg: 'var(--green-soft)' }
}
function AccountBadge({ acc }) {
  const s = accountState(acc)
  if (!s) return <span className="faint" style={{ fontSize: 12 }}>Nessun accesso</span>
  return <span className="pill pill-sm" style={{ color: s.color, background: s.bg }}>{s.label}</span>
}

// Collega un'email a un cliente; se e' gia' di un altro cliente lo dice
async function linkEmail(email, customerId, customers) {
  const e = norm(email)
  if (!EMAIL_RE.test(e)) return 'Indirizzo email non valido.'
  const { error } = await supabase.from('customer_emails').insert({ email: e, customer_id: customerId })
  if (!error) return null
  if (error.code === '23505') {
    const { data } = await supabase.from('customer_emails').select('customer_id').eq('email', e).maybeSingle()
    const other = customers.find(c => c.id === data?.customer_id)
    return `${e} è già collegata${other ? ` al cliente "${other.name}"` : ' a un altro cliente'}.`
  }
  return error.message
}

// ───────────── Riquadro nel dettaglio ticket ─────────────
export function CustomerLink({ email, onOpenCustomers }) {
  const e = norm(email)
  const [state, setState] = useState(null)   // { customer, emails, customers }
  const [mode, setMode] = useState(null)     // null | 'link' | 'new'
  const [choice, setChoice] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const { data: link, error: err } = await supabase.from('customer_emails').select('customer_id').eq('email', e).maybeSingle()
    if (err) { setState({ missing: true }); return }
    if (link) {
      const [{ data: cust }, { data: emails }] = await Promise.all([
        supabase.from('customers').select('id, name').eq('id', link.customer_id).maybeSingle(),
        supabase.from('customer_emails').select('email').eq('customer_id', link.customer_id).order('email'),
      ])
      setState({ customer: cust, emails: (emails || []).map(r => r.email) })
    } else {
      const { data: customers } = await supabase.from('customers').select('id, name').order('name')
      setState({ customer: null, customers: customers || [] })
    }
  }, [e])

  useEffect(() => { if (e) load() }, [e, load])
  if (!e || !state || state.missing) return null

  const doLink = async () => {
    if (!choice) return
    setBusy(true); setError('')
    const err = await linkEmail(e, choice, state.customers || [])
    setBusy(false)
    if (err) { setError(err); return }
    setMode(null); load()
  }
  const doNew = async () => {
    if (!name.trim()) { setError('Scrivi il nome del cliente.'); return }
    setBusy(true); setError('')
    const { data: c, error: err } = await supabase.from('customers').insert({ name: name.trim() }).select().single()
    const linkErr = err ? err.message : await linkEmail(e, c.id, [])
    setBusy(false)
    if (linkErr) { setError(linkErr); return }
    setMode(null); load()
  }

  if (state.customer) {
    const others = state.emails.filter(x => x !== e)
    return (
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
        <div className="row" style={{ gap: 6, fontSize: 13 }}>
          <Icon name="user" size={14} /> Cliente <strong className="truncate">{state.customer.name}</strong>
        </div>
        {others.length > 0 && <div className="hint" style={{ marginTop: 4 }}>Anche: {others.join(', ')}</div>}
        {onOpenCustomers && <button className="btn-link" style={{ border: 0, background: 'none', color: 'var(--accent-text)', fontSize: 12, padding: 0, marginTop: 6 }} onClick={() => onOpenCustomers(state.customer.id)}>Apri scheda cliente</button>}
      </div>
    )
  }

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      {mode === null && (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <span className="hint" style={{ width: '100%' }}>Email non collegata a un cliente</span>
          {(state.customers || []).length > 0 && <button className="btn btn-soft btn-sm" onClick={() => { setMode('link'); setError('') }}><Icon name="userPlus" size={14} /> Collega a cliente</button>}
          <button className="btn btn-secondary btn-sm" onClick={() => { setMode('new'); setName(''); setError('') }}><Icon name="plus" size={14} /> Nuovo cliente</button>
        </div>
      )}
      {mode === 'link' && (
        <>
          <select className="select select-sm" value={choice} onChange={ev => setChoice(ev.target.value)} style={{ marginBottom: 8 }}>
            <option value="">— Scegli il cliente —</option>
            {state.customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn btn-primary btn-sm" onClick={doLink} disabled={!choice || busy}>{busy ? <Spinner size={12} /> : <Icon name="check" size={14} />} Collega</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setMode(null)}>Annulla</button>
          </div>
        </>
      )}
      {mode === 'new' && (
        <>
          <input className="input input-sm" placeholder="Nome del cliente o azienda" value={name} onChange={ev => setName(ev.target.value)}
            onKeyDown={ev => ev.key === 'Enter' && doNew()} autoFocus style={{ marginBottom: 8 }} />
          <div className="row" style={{ gap: 6 }}>
            <button className="btn btn-primary btn-sm" onClick={doNew} disabled={busy}>{busy ? <Spinner size={12} /> : <Icon name="check" size={14} />} Crea e collega</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setMode(null)}>Annulla</button>
          </div>
        </>
      )}
      {error && <div className="alert alert-error" style={{ marginTop: 8, fontSize: 12 }}><Icon name="alert" size={14} /> {error}</div>}
    </div>
  )
}

// ───────────── Pagina Clienti ─────────────
export default function Customers({ openTicket, initialCustomerId, onSelect }) {
  const [customers, setCustomers] = useState(null)
  const [tickets, setTickets] = useState([])
  const [accounts, setAccounts] = useState([])
  const [selected, setSelected] = useState(initialCustomerId || null)
  // L'app ricorda il cliente aperto: tornando indietro da un suo ticket si ritrova la sua scheda
  useEffect(() => { if (onSelect) onSelect(selected && selected !== 'new' ? selected : null) }, [selected, onSelect])
  const [tab, setTab] = useState('customers')
  const [search, setSearch] = useState('')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    const [c, t, a] = await Promise.all([
      supabase.from('customers').select('id, name, notes, created_at, customer_emails(email)').order('name'),
      fetchAll(() => supabase.from('tickets').select('id, number, title, status, created_at, recipient_email').not('recipient_email', 'is', null).order('id')),
      supabase.from('portal_accounts').select('id, email, name, verified_at, disabled, created_at, last_login_at').order('created_at', { ascending: false }),
    ])
    if (c.error) { setError(/does not exist|schema cache/i.test(c.error.message) ? MISSING_SQL : c.error.message); setCustomers([]); return }
    setCustomers((c.data || []).map(x => ({ ...x, emails: (x.customer_emails || []).map(r => r.email).sort() })))
    setTickets(t.data || [])
    setAccounts(a.data || [])
  }, [])
  useEffect(() => { load() }, [load])

  const ticketsOf = (emails) => tickets.filter(t => emails.includes(norm(t.recipient_email))).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  const accountOf = (email) => accounts.find(a => a.email === email)
  const linked = new Set((customers || []).flatMap(c => c.emails))
  const customerOfEmail = (email) => (customers || []).find(c => c.emails.includes(email))

  // Email gia' usate nei ticket e non ancora collegate, le piu' frequenti prima
  const counts = {}
  tickets.forEach(t => { const e = norm(t.recipient_email); if (e) counts[e] = (counts[e] || 0) + 1 })
  const freeEmails = Object.keys(counts).filter(e => !linked.has(e)).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b))

  const q = search.trim().toLowerCase()
  const shown = (customers || []).filter(c => !q || c.name.toLowerCase().includes(q) || c.emails.some(e => e.includes(q)))
  const current = (customers || []).find(c => c.id === selected)

  const toggleBlock = async (acc) => {
    if (!acc.disabled && !window.confirm(`Bloccare l'accesso all'area clienti di ${acc.email}?`)) return
    const { error: err } = await supabase.from('portal_accounts').update({ disabled: !acc.disabled }).eq('id', acc.id)
    if (err) setError(err.message); else load()
  }

  const createFromAccount = async (acc) => {
    setError('')
    const { data: c, error: err } = await supabase.from('customers').insert({ name: acc.name || acc.email }).select().single()
    if (err) { setError(err.message); return }
    const linkErr = await linkEmail(acc.email, c.id, customers || [])
    if (linkErr) { setError(linkErr); return }
    await load()
    setTab('customers'); setSelected(c.id)
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Clienti</h1>
          <p className="page-sub">Collega le email dello stesso cliente: nell'area clienti vedrà i ticket di tutti i suoi indirizzi</p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <a className="btn btn-secondary" href="/area-clienti" target="_blank" rel="noopener noreferrer"><Icon name="arrowRight" size={15} /> Apri l'area clienti</a>
          <button className="btn btn-primary" onClick={() => { setTab('customers'); setSelected('new') }}><Icon name="plus" size={15} /> Nuovo cliente</button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 14 }}>
          <Icon name="alert" size={16} /> <span className="grow">{error}</span>
          <button className="alert-close" onClick={() => setError('')}><Icon name="x" size={14} /></button>
        </div>
      )}

      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab${tab === 'customers' ? ' active' : ''}`} onClick={() => setTab('customers')}>Clienti <span className="badge">{(customers || []).length}</span></button>
        <button className={`tab${tab === 'accounts' ? ' active' : ''}`} onClick={() => setTab('accounts')}>Accessi all'area clienti <span className="badge">{accounts.length}</span></button>
      </div>

      {customers === null ? (
        <div className="screen-center" style={{ padding: 48 }}><Spinner size={20} /></div>
      ) : tab === 'accounts' ? (
        <div className="card">
          {accounts.length === 0 ? (
            <EmptyState icon="user" title="Nessun accesso" text="Quando un cliente si registra all'area clienti compare qui." />
          ) : (
            <table className="tbl">
              <thead><tr><th>Cliente</th><th>Stato</th><th>Collegato a</th><th>Registrato</th><th>Ultimo accesso</th><th></th></tr></thead>
              <tbody>
                {accounts.map(acc => {
                  const cust = customerOfEmail(acc.email)
                  return (
                    <tr key={acc.id}>
                      <td><div className="who"><Avatar name={acc.name || acc.email} size={28} /><div className="minw0"><div className="strong truncate">{acc.name || '—'}</div><div className="who-sub truncate">{acc.email}</div></div></div></td>
                      <td><AccountBadge acc={acc} /></td>
                      <td>{cust
                        ? <button className="btn-link" style={{ border: 0, background: 'none', color: 'var(--accent-text)', padding: 0 }} onClick={() => { setTab('customers'); setSelected(cust.id) }}>{cust.name}</button>
                        : <button className="btn btn-soft btn-sm" onClick={() => createFromAccount(acc)} title="Crea un cliente con questa email; poi potrai aggiungere gli altri indirizzi"><Icon name="plus" size={13} /> Crea cliente</button>}</td>
                      <td className="c-time" title={fmtDateTime(acc.created_at)}>{timeAgo(acc.created_at)}</td>
                      <td className="c-time">{acc.last_login_at ? timeAgo(acc.last_login_at) : '—'}</td>
                      <td style={{ textAlign: 'right' }}><button className="btn btn-ghost btn-sm" onClick={() => toggleBlock(acc)}>{acc.disabled ? 'Sblocca' : 'Blocca'}</button></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
          <div className="card">
            <div style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>
              <div className="search">
                <Icon name="search" size={16} className="search-ico" />
                <input className="input" placeholder="Cerca nome o email" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
            {shown.length === 0 ? (
              <div className="empty" style={{ padding: '28px 16px' }}><div className="empty-text" style={{ marginBottom: 0 }}>{customers.length ? 'Nessun cliente trovato.' : 'Nessun cliente: creane uno con "Nuovo cliente".'}</div></div>
            ) : shown.map(c => (
              <button key={c.id} className="list-row" onClick={() => setSelected(c.id)}
                style={{ width: '100%', border: 0, borderBottom: '1px solid var(--border)', textAlign: 'left', background: selected === c.id ? 'var(--accent-soft)' : 'none', display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px' }}>
                <Avatar name={c.name} size={30} />
                <div className="minw0 grow">
                  <div className="strong truncate">{c.name}</div>
                  <div className="who-sub truncate">{c.emails.length} email · {ticketsOf(c.emails).length} ticket</div>
                </div>
                {c.emails.some(e => accountState(accountOf(e))?.label === 'Attivo') && <Icon name="user" size={14} style={{ color: 'var(--green)' }} />}
              </button>
            ))}
          </div>

          <div>
            {selected === 'new' ? (
              <NewCustomer freeEmails={freeEmails} counts={counts} customers={customers}
                onCreated={async (id) => { await load(); setSelected(id) }} onCancel={() => setSelected(null)} />
            ) : current ? (
              <CustomerCard key={current.id} customer={current} customers={customers} freeEmails={freeEmails} counts={counts}
                tickets={ticketsOf(current.emails)} accountOf={accountOf} openTicket={openTicket}
                onChanged={load} onDeleted={async () => { setSelected(null); await load() }} onToggleBlock={toggleBlock} />
            ) : (
              <div className="card"><EmptyState icon="user" title="Scegli un cliente" text="Seleziona un cliente a sinistra per vedere le sue email, i ticket e l'accesso all'area clienti." /></div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function EmailPicker({ freeEmails, counts, onAdd, busy }) {
  const [value, setValue] = useState('')
  const [open, setOpen] = useState(false)
  const v = norm(value)
  const list = freeEmails.filter(e => !v || e.includes(v)).slice(0, 8)
  const add = async (email) => { if (await onAdd(email)) setValue('') }
  return (
    <div style={{ position: 'relative' }}>
      <div className="row" style={{ gap: 8 }}>
        <div className="search grow">
          <Icon name="at" size={16} className="search-ico" />
          <input className="input" placeholder="Aggiungi un'email (scegli tra quelle dei ticket o scrivila)" value={value}
            onChange={e => { setValue(e.target.value); setOpen(true) }} onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={e => e.key === 'Enter' && v && add(v)} />
        </div>
        <button className="btn btn-secondary" onClick={() => add(v)} disabled={!v || busy}>{busy ? <Spinner size={13} /> : <Icon name="plus" size={14} />} Aggiungi</button>
      </div>
      {open && list.length > 0 && (
        <div className="pop">
          {list.map(e => (
            <div key={e} className="pop-item" onMouseDown={() => add(e)}>
              <Avatar name={e} size={22} /><span className="truncate grow">{e}</span><span className="faint" style={{ fontSize: 12 }}>{counts[e]} ticket</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function NewCustomer({ freeEmails, counts, customers, onCreated, onCancel }) {
  const [name, setName] = useState('')
  const [emails, setEmails] = useState([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const addEmail = async (email) => {
    const e = norm(email)
    if (!EMAIL_RE.test(e)) { setError('Indirizzo email non valido.'); return false }
    const owner = customers.find(c => c.emails.includes(e))
    if (owner) { setError(`${e} è già collegata al cliente "${owner.name}".`); return false }
    setError('')
    setEmails(prev => prev.includes(e) ? prev : [...prev, e])
    return true
  }

  const save = async () => {
    if (!name.trim()) { setError('Scrivi il nome del cliente.'); return }
    setBusy(true); setError('')
    const { data: c, error: err } = await supabase.from('customers').insert({ name: name.trim() }).select().single()
    if (err) { setBusy(false); setError(err.message); return }
    for (const e of emails) {
      const linkErr = await linkEmail(e, c.id, customers)
      if (linkErr) { setError(linkErr) }
    }
    setBusy(false)
    onCreated(c.id)
  }

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">Nuovo cliente</span></div>
      <div className="card-body">
        <label className="field">
          <span className="label">Nome del cliente o azienda<span className="req">*</span></span>
          <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Es. Office Service srl" autoFocus />
        </label>
        <div className="field">
          <span className="label">Email del cliente</span>
          <EmailPicker freeEmails={freeEmails.filter(e => !emails.includes(e))} counts={counts} onAdd={addEmail} />
          {emails.length > 0 && (
            <div className="p-emails" style={{ marginTop: 10 }}>
              {emails.map(e => (
                <span key={e} className="file-pill">{e}<button onClick={() => setEmails(prev => prev.filter(x => x !== e))} title="Togli"><Icon name="x" size={13} /></button></span>
              ))}
            </div>
          )}
        </div>
        {error && <div className="alert alert-error"><Icon name="alert" size={16} /> {error}</div>}
      </div>
      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onCancel}>Annulla</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? <Spinner size={14} /> : <Icon name="check" size={15} />} Crea cliente</button>
      </div>
    </div>
  )
}

function CustomerCard({ customer, customers, freeEmails, counts, tickets, accountOf, openTicket, onChanged, onDeleted, onToggleBlock }) {
  const [name, setName] = useState(customer.name)
  const [notes, setNotes] = useState(customer.notes || '')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const dirty = name.trim() !== customer.name || (notes || '') !== (customer.notes || '')

  const save = async () => {
    if (!name.trim()) { setError('Il nome non può essere vuoto.'); return }
    setBusy(true); setError('')
    const { error: err } = await supabase.from('customers').update({ name: name.trim(), notes: notes.trim() || null }).eq('id', customer.id)
    setBusy(false)
    if (err) { setError(err.message); return }
    setSaved(true); setTimeout(() => setSaved(false), 2000)
    onChanged()
  }

  const addEmail = async (email) => {
    setBusy(true); setError('')
    const err = await linkEmail(email, customer.id, customers)
    setBusy(false)
    if (err) { setError(err); return false }
    onChanged()
    return true
  }

  const removeEmail = async (email) => {
    if (!window.confirm(`Scollegare ${email} da "${customer.name}"?\nI suoi ticket restano, ma non compariranno più insieme a quelli del cliente.`)) return
    const { error: err } = await supabase.from('customer_emails').delete().eq('email', email)
    if (err) setError(err.message); else onChanged()
  }

  const remove = async () => {
    if (!window.confirm(`Eliminare il cliente "${customer.name}"?\nLe email vengono scollegate; ticket e accessi all'area clienti restano.`)) return
    const { error: err } = await supabase.from('customers').delete().eq('id', customer.id)
    if (err) setError(err.message); else onDeleted()
  }

  const open = tickets.filter(t => normStatus(t.status) !== 'risolto').length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="card">
        <div className="card-header">
          <div className="row" style={{ gap: 10 }}><Avatar name={customer.name} size={32} /><span className="card-title">{customer.name}</span></div>
          <span className="card-sub">{tickets.length} ticket · {open} in corso</span>
        </div>
        <div className="card-body">
          <div className="form-grid" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12, marginBottom: 0 }}>
            <label className="field"><span className="label">Nome</span><input className="input" value={name} onChange={e => setName(e.target.value)} /></label>
            <label className="field"><span className="label">Note interne</span><input className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Visibili solo agli operatori" /></label>
          </div>
          {(dirty || saved) && (
            <button className="btn btn-primary btn-sm" onClick={save} disabled={busy || !dirty}>{saved ? <><Icon name="check" size={14} /> Salvato</> : <><Icon name="save" size={14} /> Salva</>}</button>
          )}

          <div className="field" style={{ marginTop: 18 }}>
            <span className="label">Email collegate</span>
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 10 }}>
              {customer.emails.length === 0 && <div className="hint" style={{ padding: 12 }}>Nessuna email collegata.</div>}
              {customer.emails.map(e => {
                const acc = accountOf(e)
                return (
                  <div key={e} className="row" style={{ gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--border)' }}>
                    <Icon name="mail" size={14} style={{ color: 'var(--text-4)' }} />
                    <span className="grow truncate">{e}</span>
                    <span className="faint" style={{ fontSize: 12 }}>{counts[e] || 0} ticket</span>
                    <AccountBadge acc={acc} />
                    {acc && <button className="btn btn-ghost btn-sm" onClick={() => onToggleBlock(acc)}>{acc.disabled ? 'Sblocca' : 'Blocca'}</button>}
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => removeEmail(e)} title="Scollega"><Icon name="x" size={14} /></button>
                  </div>
                )
              })}
            </div>
            <EmailPicker freeEmails={freeEmails} counts={counts} onAdd={addEmail} busy={busy} />
            <span className="hint">Il cliente vede nell'area clienti i ticket di tutte queste email, qualunque indirizzo usi per entrare.</span>
          </div>

          {error && <div className="alert alert-error"><Icon name="alert" size={16} /> {error}</div>}
          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', marginTop: 4 }} onClick={remove}><Icon name="x" size={14} /> Elimina cliente</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Ticket</span><span className="badge">{tickets.length}</span></div>
        {tickets.length === 0 ? (
          <div className="empty" style={{ padding: '24px 16px' }}><div className="empty-text" style={{ marginBottom: 0 }}>Nessun ticket da queste email.</div></div>
        ) : (
          <table className="tbl">
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} className="trow" onClick={() => openTicket(t)} style={{ cursor: 'pointer' }}>
                  <td style={{ width: 60 }} className="faint">#{t.number}</td>
                  <td><div className="subj-title truncate" style={{ maxWidth: 420 }}>{t.title}</div>{customer.emails.length > 1 && <div className="who-sub">{norm(t.recipient_email)}</div>}</td>
                  <td style={{ width: 130 }}><StatusPill status={t.status} size="sm" /></td>
                  <td className="c-time" style={{ width: 110 }}>{timeAgo(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
